// Persistence: mid-game save/resume and a small cross-game profile (race mastery + records).
// Storage prefers the CrazyGames SDK data store (survives inside the platform iframe) and falls
// back to localStorage for local `vite dev` and other hosts. Every access is guarded so a missing
// SDK, a full quota, or a locked-down browser degrades to "no persistence" rather than a crash.

import type { GameState } from '../game/types';
import { DEFAULT_AVATAR_IDS, DEFAULT_FRAME_IDS, DEFAULT_EQUIPPED } from '../meta/catalog';
import { CAMPAIGN } from '../meta/campaign';
import { makeGuestName } from '../meta/economy';
import { useCgStore } from './crazygames';

// The ORIGINAL 10-level campaign order, frozen as literals: old profiles/saves stored progress by
// POSITION, and these are the ids those positions meant. Used once, at migration time. Never edit.
const CAMPAIGN_V1_IDS = ['c01', 'c02', 'c03', 'c04', 'c05', 'c06', 'c07', 'c08', 'c09', 'c10'] as const;

const SAVE_KEY = 'wi.save.v1';
const PROFILE_KEY = 'wi.profile.v1';
const ONBOARD_KEY = 'wi.onboarded.v1';
const SAVE_VERSION = 6; // bump when GameState shape changes so stale saves are ignored, not mis-loaded (v6: added difficulty)
const PROFILE_VERSION = 1; // profile blob schema version (checksum wrapper, v1: elmas wallet + cosmetics)
// Tamper deterrence, NOT security: this salt ships in the client bundle and is trivially extractable, so a
// determined cheater can re-sign. It only stops casual "edit the number in devtools" edits, which is all we
// need for a cosmetic-only economy that never affects other players (see docs/META-ECONOMY.md section 9).
const PROFILE_SALT = 'wi.meta.7f3ad2';

// --- raw key/value (CGData on real CrazyGames hosts with an initialized SDK, else localStorage) ---
// Gated on useCgStore, not mere presence of the SDK global: an uninitialized SDK exists on any host
// that loads the script (our mirrors), where every data call would throw + spam the console before
// falling back - and whether init succeeds there varies run to run, so mere init success must not pick
// the backend either (a flip would hide the progress earlier sessions put in localStorage; the CG store
// is a separate namespaced blob, not a passthrough). useCgStore is decided before main() touches
// storage and never changes: CG store on the platform, localStorage everywhere else, permanently.
function cgData(): CrazyGamesSDK['data'] | null {
  try {
    if (!useCgStore()) return null;
    return window.CrazyGames?.SDK?.data ?? null;
  } catch {
    return null;
  }
}

function kvGet(key: string): string | null {
  const d = cgData();
  if (d) {
    try {
      return d.getItem(key);
    } catch {
      /* fall through to localStorage */
    }
  }
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function kvSet(key: string, value: string): void {
  const d = cgData();
  if (d) {
    try {
      d.setItem(key, value);
      return;
    } catch {
      /* quota/limit - fall through to localStorage */
    }
  }
  try {
    localStorage.setItem(key, value);
  } catch {
    /* storage disabled - persistence simply won't happen */
  }
}

function kvRemove(key: string): void {
  const d = cgData();
  if (d) {
    try {
      d.removeItem(key);
    } catch {
      /* ignore */
    }
  }
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

// --- mid-game save / resume ---
// Transient fields (the last AI turn's replay buffers + log/messages + the current selection) are
// dropped: they're only meaningful within a turn, and a resumed game starts fresh at the player's turn.
type SavedState = Omit<GameState, 'replay' | 'replayBefore' | 'log' | 'messages' | 'selectedRegion'>;

export function saveGame(g: GameState, camp: number | null = null): void {
  if (g.phase !== 'playing') return; // only an in-progress game is worth resuming
  try {
    const { replay: _r, replayBefore: _rb, log: _l, messages: _m, selectedRegion: _s, ...rest } = g;
    // camp: which Conquest Trail level this match is (null = free play), stored as the LEVEL ID so a
    // reordered campaign list can never resume a save as the wrong level. Without it a resumed campaign
    // match loses its identity: the win would pay no stars, unlock nothing, and offer no "Next isle".
    kvSet(SAVE_KEY, JSON.stringify({ v: SAVE_VERSION, s: rest, camp: camp != null ? (CAMPAIGN[camp]?.id ?? null) : null }));
  } catch {
    /* serialization failed - skip this save */
  }
}

// the campaign index of the save the last loadGame() returned (null: free play, corrupt, or pre-camp save)
let loadedCamp: number | null = null;
export const savedCampaignIndex = (): number | null => loadedCamp;

/** Returns a resumable GameState, or null if there's no valid save (wrong version / corrupt / finished). */
export function loadGame(): GameState | null {
  loadedCamp = null;
  const raw = kvGet(SAVE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { v?: number; s?: SavedState; camp?: number | string | null };
    if (!parsed || parsed.v !== SAVE_VERSION || !parsed.s) return null;
    // camp is a level ID now; a legacy NUMERIC camp came from the original 10-level list, so its
    // position maps through CAMPAIGN_V1_IDS. Either way resolve to the CURRENT list's index.
    const savedId = typeof parsed.camp === 'string' ? parsed.camp : typeof parsed.camp === 'number' ? CAMPAIGN_V1_IDS[parsed.camp] : undefined;
    if (savedId) {
      const idx = CAMPAIGN.findIndex((lv) => lv.id === savedId);
      if (idx >= 0) loadedCamp = idx;
    }
    const s = parsed.s;
    // minimal shape check so a corrupt blob never reaches the engine
    if (s.phase !== 'playing' || !Array.isArray(s.regions) || !Array.isArray(s.factions) || typeof s.round !== 'number') {
      return null;
    }
    const n = s.factions.length;
    const stats = s.stats ?? { captured: new Array<number>(n).fill(0), kills: Array.from({ length: n }, () => [0, 0, 0, 0]), built: Array.from({ length: n }, () => [0, 0, 0, 0]) };
    const turnOrder = Array.isArray(s.turnOrder) && s.turnOrder.length === n ? s.turnOrder : Array.from({ length: n }, (_, i) => i);
    // fields added for multiplayer (older saves predate them): solo mode, the player on turn, no winner yet,
    // and every pending offer aimed at the (single) player faction
    const offers = (s.offers ?? []).map((o) => ({ ...o, to: o.to ?? s.playerFaction }));
    return { ...s, turnOrder, dominationPct: s.dominationPct ?? 0.6, difficulty: s.difficulty ?? 'normal', stats, mode: s.mode ?? 'solo', turnOf: s.turnOf ?? s.playerFaction, winner: s.winner ?? null, offers, mapKind: s.mapKind ?? 'isles', voyages: s.voyages ?? [], replay: [], replayBefore: [], log: [], messages: [], selectedRegion: null };
  } catch {
    return null;
  }
}

export function clearSave(): void {
  kvRemove(SAVE_KEY);
}

// --- cross-game profile: race mastery + records (drives the "play again" pull) ---
export interface RaceStat {
  games: number;
  wins: number;
  bestRound: number | null; // fewest rounds to a win with this race (lower is better)
}
export interface Profile {
  games: number;
  wins: number;
  byRace: Record<string, RaceStat>;
  tilesCaptured: number; // total tiles captured across all games (achievements)
  kills: number[]; // total enemy units slain, by unit type [Warrior, Archer, Rider, Spearman]
  built: number[]; // total units trained, by unit type
  // --- meta-economy (docs/META-ECONOMY.md). NEVER put these on GameState: elmas is a cross-game meta
  // currency and must stay out of the deterministic engine so saves/replays stay reproducible. ---
  elmas: number; // soft-currency wallet
  unlocks: { avatars: string[]; frames: string[]; sets: string[] }; // owned cosmetic ids
  equipped: { avatar: string; frame: string; skin: string }; // currently worn cosmetic ids (skin = 'set_default' or a set id)
  guestName: string; // assigned-once local name, shown until a CrazyGames login overrides it
  tutorialDone: boolean; // legacy all-done flag, now DERIVED from tutorialChapters (kept for old readers)
  tutorialChapters: string[]; // completed tutorial chapter ids (append-only; drives unlocks + 'First Lesson')
  campaignGuided: string[]; // level ids whose in-level guidance has been seen (first play only; append-only)
  itemsBought: number; // backs the 'buy N items' achievement
  // online multiplayer tallies (recorded only for REAL matches: 2+ human seats, past the opening rounds)
  mpGames: number;
  mpWins: number;
  mpKills: number; // enemy units slain across online matches (all types summed)
  // Conquest Trail progress, keyed by LEVEL ID: reordering/extending the CAMPAIGN list never
  // misattributes old stars. Source of truth; the positional array below is derived from it.
  campaignStarsById: Record<string, number>;
  // DERIVED: stars (0-3) indexed exactly like the CURRENT src/meta/campaign.ts CAMPAIGN order, rebuilt
  // from campaignStarsById on every hydrate. Kept so the many positional readers stay simple.
  campaignStars: number[];
  lastDaily: string; // YYYY-MM-DD of the last claimed daily reward ('' = never claimed)
  dailyStreak: number; // consecutive days claimed; drives the escalating payout
}

// Chapter id catalogue, mirrored from src/ui/tutorialChapters.ts (CHAPTER_IDS). Duplicated as literals on
// purpose: the platform layer must not import UI code (and achievements.ts reads purely from the Profile).
// Keep the three lists in sync.
const TUTORIAL_CHAPTER_IDS = ['basics', 'economy', 'combat', 'navy', 'council'] as const;
/** One finished game's contribution to the cross-game tallies (the player's own numbers this game). */
export interface GameTally {
  captured: number;
  kills: number[];
  built: number[];
}

// FNV-1a 32-bit: a tiny, dependency-free, deterministic hash for the tamper-deterrence checksum. Not
// cryptographic (see PROFILE_SALT) - it only has to make the stored blob non-trivial to hand-edit.
function checksum(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

function validProfileShape(p: unknown): p is Partial<Profile> {
  const q = p as Profile | null;
  return !!q && typeof q === 'object' && typeof q.games === 'number' && typeof q.wins === 'number' && !!q.byRace && typeof q.byRace === 'object';
}

const uniq = (arr: readonly string[]): string[] => [...new Set(arr)];

// Fill every field with a safe default (missing fields on old/partial blobs never read as undefined). The
// 5 race-default avatars + the plain frame are always folded in, so a player can never lose their free
// cosmetics. When `tampered` is set (a bad checksum), only the economy-sensitive fields reset to baseline;
// stats/achievements/tutorial progress survive so a hand-migrated or corrupt blob never bricks a legit player.
function hydrateProfile(p: Partial<Profile>, tampered: boolean): Profile {
  const prof: Profile = {
    games: p.games ?? 0,
    wins: p.wins ?? 0,
    byRace: p.byRace ?? {},
    tilesCaptured: p.tilesCaptured ?? 0,
    kills: Array.isArray(p.kills) ? p.kills : [0, 0, 0, 0],
    built: Array.isArray(p.built) ? p.built : [0, 0, 0, 0],
    elmas: typeof p.elmas === 'number' && p.elmas >= 0 ? Math.floor(p.elmas) : 0,
    unlocks: {
      avatars: uniq([...DEFAULT_AVATAR_IDS, ...(p.unlocks?.avatars ?? [])]),
      frames: uniq([...DEFAULT_FRAME_IDS, ...(p.unlocks?.frames ?? [])]),
      sets: uniq(p.unlocks?.sets ?? []),
    },
    equipped: {
      avatar: p.equipped?.avatar ?? DEFAULT_EQUIPPED.avatar,
      frame: p.equipped?.frame ?? DEFAULT_EQUIPPED.frame,
      skin: p.equipped?.skin ?? DEFAULT_EQUIPPED.skin,
    },
    guestName: p.guestName || makeGuestName(),
    tutorialDone: !!p.tutorialDone,
    // MIGRATION: an old graduate (legacy tutorialDone, no chapter list) keeps their 'First Lesson' badge:
    // seed all 5 ids so the achievement's count never dips below its threshold (which would silently drop
    // the badge and later double-pay via newlyUnlocked). Ships in lockstep with the achievements.ts rework.
    tutorialChapters: (() => {
      const raw = Array.isArray(p.tutorialChapters) ? uniq(p.tutorialChapters.filter((id): id is string => typeof id === 'string')) : [];
      return raw.length === 0 && p.tutorialDone ? [...TUTORIAL_CHAPTER_IDS] : raw;
    })(),
    campaignGuided: Array.isArray(p.campaignGuided) ? uniq(p.campaignGuided.filter((id): id is string => typeof id === 'string')) : [],
    itemsBought: typeof p.itemsBought === 'number' && p.itemsBought >= 0 ? Math.floor(p.itemsBought) : 0,
    mpGames: typeof p.mpGames === 'number' && p.mpGames >= 0 ? Math.floor(p.mpGames) : 0,
    mpWins: typeof p.mpWins === 'number' && p.mpWins >= 0 ? Math.floor(p.mpWins) : 0,
    mpKills: typeof p.mpKills === 'number' && p.mpKills >= 0 ? Math.floor(p.mpKills) : 0,
    // stars: id-keyed going forward. A pre-Broken-Crown profile only has the positional array, whose
    // indices meant the ORIGINAL c01..c10 order: migrate those stars onto their ids exactly once.
    campaignStarsById: (() => {
      const clean = (s: unknown): number => (typeof s === 'number' && s > 0 ? Math.min(3, Math.floor(s)) : 0);
      if (p.campaignStarsById && typeof p.campaignStarsById === 'object') {
        const out: Record<string, number> = {};
        for (const [id, s] of Object.entries(p.campaignStarsById)) if (clean(s) > 0) out[id] = clean(s);
        return out;
      }
      const out: Record<string, number> = {};
      if (Array.isArray(p.campaignStars)) {
        p.campaignStars.forEach((s, i) => {
          const id = CAMPAIGN_V1_IDS[i];
          if (id && clean(s) > 0) out[id] = clean(s);
        });
      }
      return out;
    })(),
    campaignStars: [], // derived below, once campaignStarsById is settled
    lastDaily: typeof p.lastDaily === 'string' ? p.lastDaily : '',
    dailyStreak: typeof p.dailyStreak === 'number' && p.dailyStreak > 0 ? Math.floor(p.dailyStreak) : 0,
  };
  if (tampered) {
    prof.elmas = 0;
    prof.unlocks = { avatars: [...DEFAULT_AVATAR_IDS], frames: [...DEFAULT_FRAME_IDS], sets: [] };
    prof.equipped = { avatar: DEFAULT_EQUIPPED.avatar, frame: DEFAULT_EQUIPPED.frame, skin: DEFAULT_EQUIPPED.skin };
    prof.itemsBought = 0;
  }
  // never leave a cosmetic equipped that isn't owned (tampered-in, or reset above). The default skin is
  // always available, so only a race set has to be owned to stay equipped.
  if (!prof.unlocks.avatars.includes(prof.equipped.avatar)) prof.equipped.avatar = DEFAULT_EQUIPPED.avatar;
  if (!prof.unlocks.frames.includes(prof.equipped.frame)) prof.equipped.frame = DEFAULT_EQUIPPED.frame;
  if (prof.equipped.skin !== DEFAULT_EQUIPPED.skin && !prof.unlocks.sets.includes(prof.equipped.skin)) prof.equipped.skin = DEFAULT_EQUIPPED.skin;
  prof.campaignStars = CAMPAIGN.map((lv) => prof.campaignStarsById[lv.id] ?? 0); // positional view of the id-keyed truth
  return prof;
}

const freshProfile = (): Profile => hydrateProfile({}, false);

export function loadProfile(): Profile {
  const raw = kvGet(PROFILE_KEY);
  let prof: Profile | null = null;
  let hadGuestName = false; // did the STORED blob already carry a guest name? (so we never re-roll it)
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as { v?: number; sum?: number; data?: unknown } | Partial<Profile>;
      // new checksum-wrapped format { v, sum, data }
      if (parsed && typeof parsed === 'object' && 'data' in parsed && parsed.data && typeof parsed.data === 'object') {
        if (validProfileShape(parsed.data)) {
          const tampered = checksum(JSON.stringify(parsed.data) + PROFILE_SALT) !== parsed.sum;
          hadGuestName = typeof parsed.data.guestName === 'string' && parsed.data.guestName.length > 0;
          prof = hydrateProfile(parsed.data, tampered);
        }
      } else if (validProfileShape(parsed)) {
        // legacy bare profile from before the checksum wrapper: trust it once, it re-signs on the next write
        hadGuestName = typeof parsed.guestName === 'string' && parsed.guestName.length > 0;
        prof = hydrateProfile(parsed, false);
      }
    } catch {
      /* corrupt - start fresh */
    }
  }
  if (!prof) {
    // an unreadable blob (truncated write, failed shape check) is about to be REPLACED by a fresh
    // profile on this very read: park the raw bytes under a backup key first, so a legit player's
    // progress is at least recoverable instead of silently destroyed
    if (raw) kvSet(`${PROFILE_KEY}.bak`, raw);
    prof = freshProfile();
  }
  // A guest name is assigned ONCE and must stick. The first time we mint one (no stored name), persist it
  // immediately so it never re-rolls between views/sessions; afterwards the stored name is reused as-is.
  if (!hadGuestName) persistProfile(prof);
  return prof;
}

// A cloud-sync layer (Sign in with itch.io) can register ONE listener on the single write path below:
// every persisted change schedules a debounced push, so the cloud copy follows local play without any
// call-site changes. Registered lazily by src/platform/itch.ts only when an itch login exists.
let persistListener: ((p: Profile) => void) | null = null;
export function onProfilePersist(cb: ((p: Profile) => void) | null): void {
  persistListener = cb;
}

// Single write path: wraps the profile as { v, sum, data } so loadProfile can verify it. Best-effort
// (kvSet swallows quota/disabled errors), so callers treat the returned in-memory profile as authoritative
// for the session and never block on the write landing.
function persistProfile(p: Profile): void {
  try {
    const data = JSON.stringify(p);
    kvSet(PROFILE_KEY, JSON.stringify({ v: PROFILE_VERSION, sum: checksum(data + PROFILE_SALT), data: p }));
  } catch {
    /* persistence best-effort */
  }
  try {
    persistListener?.(p);
  } catch {
    /* a sync hiccup must never break a local save */
  }
}

/** Replace the local profile with an externally produced one (the itch cloud merge). Runs the full
 *  hydrate so a malformed or older-shaped cloud blob can never brick the local state. */
export function importProfile(p: Partial<Profile>): Profile {
  const prof = hydrateProfile(p, false);
  persistProfile(prof);
  return prof;
}

/** Credit elmas (match reward, rewarded-ad bonus, achievement payout). Returns the updated profile. */
export function addElmas(n: number): Profile {
  const p = loadProfile();
  p.elmas = Math.max(0, p.elmas + Math.floor(n));
  persistProfile(p);
  return p;
}

/** Daily reward: pays once per calendar day, escalating while the player returns on consecutive days
 *  (a missed day resets the streak). Auto-claimed on the first load of the day, so the value always
 *  lands even if the announcement card is ignored. Returns what was paid, or claimed:false if already
 *  collected today. Local calendar days on purpose: the player's "tomorrow" is their own midnight. */
export function claimDaily(): { claimed: boolean; amount: number; streak: number } {
  const p = loadProfile();
  const dayKey = (d: Date): string => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const today = dayKey(new Date());
  if (p.lastDaily === today) return { claimed: false, amount: 0, streak: p.dailyStreak };
  // local calendar arithmetic, NOT now-24h: on a DST spring-forward day the previous local day was only
  // 23 real hours, so an epoch offset lands two days back in the first hour after midnight and would
  // silently reset the streak. The Date constructor normalizes day 0 across month/year bounds.
  const nowD = new Date();
  const yesterday = dayKey(new Date(nowD.getFullYear(), nowD.getMonth(), nowD.getDate() - 1));
  const streak = p.lastDaily === yesterday ? Math.min(7, p.dailyStreak + 1) : 1;
  const amount = 20 + (streak - 1) * 10; // day 1: 20, day 7+: 80
  p.lastDaily = today;
  p.dailyStreak = streak;
  p.elmas = Math.max(0, p.elmas + amount);
  persistProfile(p);
  return { claimed: true, amount, streak };
}

/** Record a Conquest Trail result: a level's star count only ever goes UP (a slower replay never demotes
 *  an earned rating). Returns how many NEW stars this run added, so the caller pays exactly once for them. */
export function recordCampaignStars(index: number, stars: number): { profile: Profile; gained: number } {
  const p = loadProfile();
  const id = CAMPAIGN[index]?.id;
  if (!id || stars <= 0) return { profile: p, gained: 0 };
  const had = p.campaignStarsById[id] ?? 0;
  const now = Math.min(3, Math.max(had, Math.floor(stars)));
  const gained = now - had;
  if (gained > 0) {
    p.campaignStarsById[id] = now;
    p.campaignStars = CAMPAIGN.map((lv) => p.campaignStarsById[lv.id] ?? 0);
    persistProfile(p);
  }
  return { profile: p, gained };
}

/** Buy a cosmetic atomically: charge elmas, add to owned, count the purchase. Returns the updated profile,
 *  or null when the balance is too low. No-op charge if already owned. */
export function purchaseCosmetic(kind: 'avatars' | 'frames' | 'sets', id: string, price: number): Profile | null {
  const p = loadProfile();
  if (p.unlocks[kind].includes(id)) return p; // already owned - never double-charge
  if (p.elmas < price) return null;
  p.elmas -= Math.floor(price);
  p.unlocks[kind].push(id);
  p.itemsBought += 1;
  if (kind === 'sets') p.equipped.skin = id; // buying a War Regalia equips it immediately (revert via the Default skin)
  persistProfile(p);
  return p;
}

/** Equip an owned cosmetic (ignored if not owned). A skin equips via `kind: 'set'` and the always-owned
 *  'set_default' is accepted so a player can revert to the base units. Returns the updated profile. */
export function equipCosmetic(kind: 'avatar' | 'frame' | 'set', id: string): Profile {
  const p = loadProfile();
  if (kind === 'set') {
    if (id === DEFAULT_EQUIPPED.skin || p.unlocks.sets.includes(id)) p.equipped.skin = id;
  } else {
    const owned = kind === 'avatar' ? p.unlocks.avatars : p.unlocks.frames;
    if (owned.includes(id)) p.equipped[kind] = id;
  }
  persistProfile(p);
  return p;
}

/** Mark one tutorial chapter completed (append-only, idempotent; replays never add anything). When all 5
 *  chapters are in, the legacy tutorialDone flag flips too: it stays the derived all-done value so
 *  isFirstTime()-era readers and old profile consumers keep working. */
/** Record that a campaign level's in-level guidance ran on this profile (complete OR skipped: either
 *  way the player has met it, and a guide must never nag a replay). Append-only, idempotent. */
export function markCampaignGuided(id: string): Profile {
  const p = loadProfile();
  if (!p.campaignGuided.includes(id)) {
    p.campaignGuided = [...p.campaignGuided, id];
    persistProfile(p);
  }
  return p;
}

export function markTutorialChapterDone(id: string): Profile {
  const p = loadProfile();
  if (!p.tutorialChapters.includes(id)) p.tutorialChapters.push(id);
  if (TUTORIAL_CHAPTER_IDS.every((c) => p.tutorialChapters.includes(c))) p.tutorialDone = true;
  persistProfile(p);
  return p;
}

/** Record a finished ONLINE match (caller applies the real-match guards); returns the updated profile. */
export function recordOnlineResult(win: boolean, kills: number): Profile {
  const p = loadProfile();
  p.mpGames += 1;
  if (win) p.mpWins += 1;
  p.mpKills += Math.max(0, Math.floor(kills));
  persistProfile(p);
  return p;
}

/** Record a finished game and persist; returns the updated profile. */
export function recordResult(race: string, win: boolean, round: number, tally?: GameTally): Profile {
  const p = loadProfile();
  p.games += 1;
  if (win) p.wins += 1;
  const r = (p.byRace[race] ??= { games: 0, wins: 0, bestRound: null });
  r.games += 1;
  if (win) {
    r.wins += 1;
    if (r.bestRound == null || round < r.bestRound) r.bestRound = round;
  }
  if (tally) {
    p.tilesCaptured += tally.captured;
    for (let i = 0; i < 4; i++) {
      p.kills[i] = (p.kills[i] ?? 0) + (tally.kills[i] ?? 0);
      p.built[i] = (p.built[i] ?? 0) + (tally.built[i] ?? 0);
    }
  }
  persistProfile(p);
  return p;
}

// --- first-time flag (drives the one-time teach-by-doing coach on the very first game) ---
export function isFirstTime(): boolean {
  return kvGet(ONBOARD_KEY) == null;
}
export function markOnboarded(): void {
  kvSet(ONBOARD_KEY, '1');
}
